<template>
  <div class="trip-annotations-chart">
    <div class="annotation-axis" :style="{ 'width': `${axisWidth}px`}">
      <div
        class="tick"
        v-for="(item, index) in keys"
        :key="index"
        :style="{ 'transform': `translateY(${y(item)}px)`, 'height': `${barHeight}px` }"
      >
        <i class="material-icons" v-if="icons[item]">{{ icons[item] }}</i>
        <span class="text">{{ $t(`trip.annotations.${item}`) }}</span>
      </div>
    </div>
    <div class="annotation-chart">
      <svg
        :width="svgWidth + margin.left + margin.right"
        :height="height + margin.top + margin.bottom"
      >
        <line
          v-if="hoveredItem"
          class="hover-line"
          :x1="x(hoveredItem.waypoint.timestamp) + margin.left"
          :x2="x(hoveredItem.waypoint.timestamp) + margin.left"
          :y1="0 - margin.top"
          :y2="height + (margin.top * 2)"
        ></line>
        <line
          v-if="timestamp"
          class="hover-line"
          :x1="x(timestamp) + margin.left"
          :x2="x(timestamp) + margin.left"
          :y1="0 - margin.top"
          :y2="height + (margin.top * 2)"
        ></line>
        <g ref="g" :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)` }"></g>
        <rect
          ref="overlay"
          class="overlay"
          :width="svgWidth"
          :height="height"
          :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)` }"
        ></rect>
      </svg>
      <div
        class="tooltip"
        v-if="hoveredItem && hoveredItem.annotation"
        :style="{ 'transform': `translate(${x(hoveredItem.waypoint.timestamp) + margin.left}px, ${y(hoveredItem.annotation.value) + margin.top + barHeight}px)` }"
      >
        <div class="tooltip-inner">
          <div class="magnitude" v-if="hoveredItem.annotation.magnitude">
            <div class="magnitude-axis level">
              <div class="level-left">{{ $t('trip.overview.chart.low') }}</div>
              <div class="level-right">{{ $t('trip.overview.chart.high') }}</div>
            </div>
            <div class="magnitude-bg">
              <div class="magnitude-value" :style="{ 'left': `${magnitudePct}%` }"></div>
            </div>
          </div>
          <div class="level">
            <div class="level-left">
              <i class="material-icons">&#xE5C8;</i>
              {{ $t('trip.overview.chart.speed') }}
            </div>
            <div
              class="level-right"
            >{{ $t('ui.values.km_per_hour', [humanizeSpeed(hoveredItem.waypoint.speed)]) }}</div>
          </div>
          <div class="level">
            <div class="level-left">
              <i class="material-icons">&#xE192;</i>
              {{ $t('trip.overview.chart.time') }}
            </div>
            <div class="level-right">{{ humanizeTimestamp(hoveredItem.waypoint.timestamp) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { reduce, has, isEqual, find } from 'lodash';
import * as moment from 'moment';
import { scaleTime, scaleBand, scaleLinear } from 'd3-scale';
import { select, mouse } from 'd3-selection';
import { rgb } from 'd3-color';
import { interpolateHcl } from 'd3-interpolate';
import { bisector } from 'd3-array';
import { humanize } from 'utils/string';
import icons from 'utils/icons';

export default {
  name: 'TripAnnotationChart',

  props: {
    start: {
      type: Date,
      required: true,
    },
    end: {
      type: Date,
      required: true,
    },
    annotations: {
      type: Array,
      required: true,
    },
    waypoints: {
      type: Array,
      required: true,
    },
    width: {
      type: Number,
      required: true,
    },
    timestamp: {
      type: Date,
      required: false,
    },
  },

  data() {
    return {
      keys: [
        'accelerate',
        'brake',
        'coast',
        'lane_switch',
        'left_turn',
        'right_turn',
        'roundabout',
        'phone_handling',
      ],
      svgWidth: 0,
      height: 0,
      margin: { top: 16, bottom: 16, left: 16, right: 24 },
      barHeight: 20,
      rowHeight: 30,
      axisWidth: 144,
      x: scaleTime(),
      y: scaleBand(),
      color: scaleLinear(),
      tooltip: null,
      hoveredItem: null,
      icons: icons,
    };
  },

  methods: {
    humanize(value) {
      return humanize(value);
    },
    humanizeSpeed(speed) {
      return Math.round(speed);
    },
    humanizeTimestamp(timestamp) {
      return moment(timestamp).format('h:mm a');
    },
    setHoveredItem(item) {
      this.hoveredItem = item;
    },
    emitWaypoint(waypoint) {
      this.$emit('hovered-annotations-chart', waypoint);
    },
  },

  computed: {
    magnitudePct() {
      if (!has(this.hoveredItem, 'annotation.magnitude')) {
        return;
      }

      const offsetRight = (167 / 176) * 100;
      const offsetLeft = (9 / 176) * 100;
      const pct = this.hoveredItem.annotation.magnitude * 100;

      if (pct > offsetRight) {
        return offsetRight;
      }

      if (pct < offsetLeft) {
        return offsetLeft;
      }

      return pct;
    },
  },

  mounted() {
    this.svgWidth =
      this.width - this.axisWidth - this.margin.left - this.margin.right;
    this.height = this.keys.length * this.rowHeight;

    this.x.range([0, this.svgWidth]).domain([this.start, this.end]);

    this.y.range([0, this.height]).domain(this.keys);

    this.color
      .interpolate(interpolateHcl)
      .range([rgb('#bbdefb'), rgb('#426bb7'), rgb('#000249')])
      .domain([0, 0.5, 1]);

    const g = select(this.$refs.g);

    const rect = g.selectAll('rect').data(this.annotations);

    const x = this.x;
    const waypoints = this.waypoints;
    const setHoveredItem = this.setHoveredItem;
    const emitWaypoint = this.emitWaypoint;

    rect
      .enter()
      .append('rect')
      .attr('x', d => this.x(d._start))
      .attr('y', d => this.y(d.value))
      .attr('width', d => this.x(d._end) - this.x(d._start))
      .style(
        'fill',
        d => (has(d, 'magnitude') ? this.color(d.magnitude) : this.color(0.5))
      )
      .attr('height', this.barHeight)
      .attr('rx', 1)
      .attr('ry', 1);

    rect.exit().remove();

    select(this.$refs.overlay)
      .on('mouseover', d => {})
      .on('mouseout', d => {
        this.hoveredItem = null;
        this.$emit('hovered-annotations-chart', null);
      })
      .on('mousemove', () => {
        const bisectDate = bisector(d => d.timestamp).left;
        const pos = mouse(this.$refs.overlay);
        const x0 = this.x.invert(pos[0]);
        const i = bisectDate(this.waypoints, x0, 1);
        const d0 = this.waypoints[i - 1];
        const d1 = this.waypoints[i];
        let waypoint;

        if (d1) {
          waypoint = x0 - d0.time > d1.time - x0 ? d1 : d0;
        } else {
          waypoint = d0;
        }

        const eachBand = this.y.step();
        const index = Math.round(pos[1] / eachBand);
        const val = this.y.domain()[index];

        const annotation = find(this.annotations, item => {
          return item.value === val && x0 >= item._start && x0 <= item._end;
        });

        if (annotation) {
          g.selectAll('rect').each(function(h) {
            if (isEqual(annotation, h)) {
              return;
            }

            select(this).style('opacity', 0.24);
          });
        } else {
          g.selectAll('rect').each(function() {
            select(this).style('opacity', 1);
          });
        }

        emitWaypoint(waypoint);
        setHoveredItem({ annotation, waypoint });
      });
  },

  watch: {
    width: function(newWidth) {
      if (newWidth === 0) {
        return;
      }

      this.svgWidth = this.width - this.margin.left - this.margin.right - 144;
      this.x.range([0, this.svgWidth]);

      select(this.$refs.g)
        .selectAll('rect')
        .attr('x', d => this.x(d._start))
        .attr('width', d => this.x(d._end) - this.x(d._start));
    },
  },
};
</script>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.trip-annotations-chart {
  display: flex;
  flex: 1;

  .annotation-chart {
    rect {
      cursor: pointer;
      transition: opacity 0.1s $timing-function;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.annotation-axis {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.annotation-axis {
  position: relative;
  padding-left: 0.875rem;
  padding-right: 1rem;
  background-color: rgba($black, 0.02);
  border-right: 1px solid rgba($black, 0.08);

  .tick {
    position: absolute;
    display: flex;

    .text,
    i {
      font-size: 0.875rem;
      line-height: 1.29;
      color: rgba($black, 0.87);
    }

    i {
      + .text {
        margin-left: 0.25rem;
      }
    }
  }
}

.annotation-chart {
  position: relative;
  display: flex;
}

.hover-line {
  stroke-width: 1px;
  stroke: #bcbcbc;
  pointer-events: none;
}

.tooltip {
  opacity: 1;
  top: 1rem;
  left: 1rem;
  transition: none;

  .level:not(.magnitude-axis) {
    font-size: $size-small;
    line-height: 1.5;

    &:not(:last-child) {
      margin-bottom: 0.25rem;
    }

    .level-left {
      font-weight: $weight-medium;
      color: rgba($black, 0.87);

      & + .level-right {
        margin-left: 2.25rem;
      }
    }

    .level-right {
      color: rgba($black, 0.64);
    }
  }

  i {
    font-size: $size-small;
    margin-right: 0.5rem;
  }
}

.tooltip-inner {
  padding: 0.75rem;

  &::after {
    display: none;
  }
}

.magnitude {
  &:not(:last-child) {
    margin-bottom: 0.875rem;
  }

  .level {
    margin-bottom: 0.25rem;
    font-size: 0.5rem;
    font-weight: $weight-medium;
    line-height: 1;
    letter-spacing: 1.5px;
    color: rgba($black, 0.64);
    text-transform: uppercase;
  }
}

.magnitude-bg {
  position: relative;
  width: 11rem;
  height: 0.5rem;
  background: linear-gradient(to right, #bbdefb, #426bb7 50%, #000249);
  border-radius: 2px;
}

.magnitude-value {
  position: absolute;
  width: 1rem;
  height: 0.375rem;
  top: 1px;
  transform: translateX(-50%);
  background-color: $white;
  border-radius: 1px;
}

.overlay {
  fill: none;
  pointer-events: all;
}
</style>

